import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart as LineChartIcon, ShoppingCart } from 'lucide-react';
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceLine } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Price Forecast Panel — last Nm RM rate trend + 3m forecast + buy signal + one-click PO draft
export default function PriceForecastPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [months, setMonths] = React.useState(12);
  const [signalFilter, setSignalFilter] = React.useState('all');
  const [expanded, setExpanded] = React.useState({});
  const [buyBusy, setBuyBusy] = React.useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/insights/price-forecast?months=${months}`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [months]);

  const buyNow = async (mat, e) => {
    e.stopPropagation();
    if (!window.confirm(`Create a draft PO for ${mat.name} at ₹${mat.current_rate}/${mat.unit} now? You can review & send from Purchases.`)) return;
    setBuyBusy((s) => ({ ...s, [mat.material_id]: true }));
    try {
      const res = await axios.post(`${API}/auto-po/buy-now/${mat.material_id}`, {}, { withCredentials: true });
      const po = res.data.po;
      toast.success(`Draft PO created · ${po.id}${po.vendor_name ? ' · ' + po.vendor_name : ''}`);
      // Navigate to Purchases so the user can review
      window.location.href = '/purchases';
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to create draft PO');
    } finally {
      setBuyBusy((s) => ({ ...s, [mat.material_id]: false }));
    }
  };

  if (loading) return <div className="text-zinc-400 text-sm py-6">Loading forecast…</div>;
  if (!data) return null;

  const filtered = signalFilter === 'all' ? data.materials : data.materials.filter((m) => m.signal === signalFilter);

  const signalStyle = {
    buy_now: 'bg-red-900/30 text-red-300 border-red-700',
    wait:    'bg-green-900/30 text-green-300 border-green-700',
    stable:  'bg-zinc-800 text-zinc-300 border-zinc-700',
  };
  const signalLabel = { buy_now: '🔴 Buy Now', wait: '🟢 Wait', stable: '⚪ Stable' };

  return (
    <div className="space-y-4" data-testid="forecast-panel">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-zinc-50 font-heading font-semibold text-sm flex items-center gap-2">
            <LineChartIcon size={14} className="text-cyan-400" /> Raw-Material Price Forecast
          </h3>
          <p className="text-zinc-500 text-xs mt-0.5">
            Last {data.months}-month trend · 3-month forecast · {data.total_materials} materials with history ·{' '}
            <span className="text-red-400">{data.buy_now_count} Buy Now</span> ·{' '}
            <span className="text-green-400">{data.wait_count} Wait</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="forecast-months-select">
            {[6, 12, 18, 24].map(n => <option key={n} value={n}>{n} months</option>)}
          </select>
          <select value={signalFilter} onChange={(e) => setSignalFilter(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="forecast-signal-filter">
            <option value="all">All signals</option>
            <option value="buy_now">Buy Now</option>
            <option value="wait">Wait</option>
            <option value="stable">Stable</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-zinc-500 text-xs py-10 bg-zinc-900/40 border border-zinc-800 rounded-xl">
          {data.total_materials === 0
            ? "No priced purchase history yet. The forecast fills in from purchase bill lines, purchase orders and raw-material rate updates."
            : 'No materials match this filter.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <div key={m.material_id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`forecast-row-${m.material_id}`}>
              <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-800/40"
                   onClick={() => setExpanded(s => ({ ...s, [m.material_id]: !s[m.material_id] }))}>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 text-sm font-semibold">{m.name}</div>
                  <div className="text-zinc-500 text-[11px] mt-0.5 flex flex-wrap items-center gap-x-1.5" data-testid={`forecast-coverage-${m.material_id}`}>
                    <span>Current ₹{m.current_rate}/{m.unit}</span>
                    <span>· {m.actual_points ?? m.series.length} actual prices across {m.coverage_months ?? m.series.length}m</span>
                    {m.sources && <span className="text-zinc-600">({[m.sources.purchase_bills ? `${m.sources.purchase_bills} bill lines` : null, m.sources.purchase_orders ? `${m.sources.purchase_orders} PO lines` : null, m.sources.price_audits ? `${m.sources.price_audits} rate edits` : null].filter(Boolean).join(', ')})</span>}
                    <span className={`px-1.5 rounded border text-[10px] ${m.confidence === 'high' ? 'border-emerald-700 text-emerald-300' : m.confidence === 'medium' ? 'border-amber-700 text-amber-300' : 'border-zinc-700 text-zinc-400'}`}>{m.confidence || 'low'} confidence</span>
                    {m.uncertainty_band > 0 && <span className="text-zinc-600">±₹{m.uncertainty_band}</span>}
                    <span>· 12m change{' '}
                    <span className={m.change_pct > 5 ? 'text-red-300' : m.change_pct < -5 ? 'text-green-300' : 'text-zinc-400'}>
                      {m.change_pct > 0 ? '+' : ''}{m.change_pct}%
                    </span></span>
                  </div>
                </div>
                <div className="text-right flex items-center gap-2">
                  {m.signal === 'buy_now' && (
                    <button
                      onClick={(e) => buyNow(m, e)}
                      disabled={buyBusy[m.material_id]}
                      data-testid={`forecast-buy-now-${m.material_id}`}
                      className="text-[11px] px-2.5 py-1 rounded-lg bg-red-500 hover:bg-red-400 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-semibold flex items-center gap-1 transition-all"
                      title={`Create draft PO now at ₹${m.current_rate}/${m.unit} · sent to Purchases for review`}>
                      <ShoppingCart size={11} /> {buyBusy[m.material_id] ? 'Creating…' : 'Lock Rate'}
                    </button>
                  )}
                  <div>
                    <div className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${signalStyle[m.signal]}`}>
                      {signalLabel[m.signal]}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                      {m.monthly_trend_pct > 0 ? '+' : ''}{m.monthly_trend_pct}%/month
                    </div>
                  </div>
                </div>
              </div>
              {expanded[m.material_id] && (
                <div className="border-t border-zinc-800 p-3 bg-zinc-950/60">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{m.advice}</div>
                  {(m.forecast || []).length === 0 && <div className="text-[11px] text-amber-300/80 mb-1" data-testid={`forecast-none-${m.material_id}`}>No forecast — fewer than 2 months of actual purchase prices.</div>}
                  {(m.unit_notes || []).length > 0 && <div className="text-[10px] text-zinc-500 mb-1">Unit note: {m.unit_notes.join('; ')}</div>}
                  <div className="text-[10px] text-zinc-600 mb-1">Solid = actual purchase prices (per {m.unit}). Dashed = forecast{m.uncertainty_band > 0 ? ` with ±₹${m.uncertainty_band} band` : ''}.</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={[
                      ...m.series.map((s) => ({ month: s.month, rate: s.avg_rate, forecast: null })),
                      ...(m.forecast || []).map((f) => ({ month: f.month, rate: null, forecast: f.forecast_rate, low: f.low, high: f.high })),
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="month" stroke="#71717a" fontSize={10} />
                      <YAxis stroke="#71717a" fontSize={10} domain={['dataMin * 0.95', 'dataMax * 1.05']} />
                      <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }} formatter={(v) => v ? `₹${v}` : '—'} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <ReferenceLine y={m.current_rate} stroke="#f59e0b" strokeDasharray="2 4" label={{ value: `Current ₹${m.current_rate}`, fill: '#f59e0b', fontSize: 10, position: 'right' }} />
                      <Line type="monotone" dataKey="rate" stroke="#22d3ee" strokeWidth={2} name="Historical" dot={{ r: 3 }} connectNulls={false} />
                      <Line type="monotone" dataKey="forecast" stroke="#a855f7" strokeWidth={2} strokeDasharray="5 5" name="Forecast (3m)" dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="low" stroke="#a855f7" strokeWidth={1} strokeOpacity={0.4} strokeDasharray="2 4" name="Forecast low" dot={false} legendType="none" />
                      <Line type="monotone" dataKey="high" stroke="#a855f7" strokeWidth={1} strokeOpacity={0.4} strokeDasharray="2 4" name="Forecast high" dot={false} legendType="none" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
